import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const BREAK_GLASS_ENABLED = String(
  process.env.REACT_APP_BREAK_GLASS_LOCAL_AUTH_ENABLED || "false",
).toLowerCase() === "true";

export const LoginPage = () => {
  const [username] = useState("sales");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [msLoading, setMsLoading] = useState(false);
  const [showBreakGlass, setShowBreakGlass] = useState(false);
  const { login, loginWithMicrosoft, token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (token) navigate("/");
  }, [token, navigate]);

  useEffect(() => {
    if (!BREAK_GLASS_ENABLED) return undefined;

    const handleShortcut = (event) => {
      if (
        event.ctrlKey
        && event.altKey
        && event.shiftKey
        && event.key.toLowerCase() === "l"
      ) {
        event.preventDefault();
        setShowBreakGlass((value) => !value);
        setPassword("");
      }
    };

    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await login(username, password);
      toast.success("Welcome back!");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setMsLoading(true);
    try {
      await loginWithMicrosoft();
      toast.success("Welcome!");
    } catch (error) {
      if (error.errorCode !== "user_cancelled") {
        toast.error("Microsoft login failed. Please try again.");
      }
    } finally {
      setMsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card animate-fade-in">
        <div className="text-center mb-8">
          <div className="bg-white rounded-lg p-3 inline-block mb-4">
            <img
              src="https://www.gamerpackaging.com/wp-content/uploads/GamerPackaging_FullLogo_v1.png"
              alt="Gamer Packaging"
              className="h-12 w-auto"
            />
          </div>
          <h1 className="text-xl font-bold mb-2">InkSight</h1>
          <p className="text-muted-foreground text-sm">Sign in to continue</p>
        </div>

        <button
          data-testid="microsoft-login-btn"
          onClick={handleMicrosoftLogin}
          disabled={msLoading}
          className="w-full py-3 flex items-center justify-center gap-3 bg-[#2F2F2F] hover:bg-[#404040] text-white rounded-lg font-medium transition-colors disabled:opacity-50 border border-gray-600"
        >
          <svg className="w-5 h-5" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="1" y="1" width="9" height="9" fill="#F25022" />
            <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
            <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
            <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
          </svg>
          {msLoading ? "Signing in..." : "Sign in with Microsoft"}
        </button>

        {BREAK_GLASS_ENABLED && showBreakGlass && (
          <>
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">Local Test Login</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Username</label>
                <input
                  data-testid="login-username"
                  type="text"
                  value={username}
                  readOnly
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-muted-foreground"
                  aria-label="Local test username"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Password</label>
                <input
                  data-testid="login-password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
                  placeholder="Enter local test password"
                  autoComplete="current-password"
                  autoFocus
                  required
                />
              </div>

              <button
                data-testid="login-submit"
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
